import { useCallback, useState } from 'react';

import { resolveFileUrl } from '@/api/client';
import { Icon } from '@/components/ui';
import { cn } from '@/utils/cn';

/**
 * The company's logo in a light bordered rounded square -- the tile a job
 * board puts beside a listing's title. One component for the list cards and
 * the listing page, so a missing logo looks the same everywhere.
 *
 * The placeholder (a building on a light brand tint) is always painted, and
 * the image only covers it once it has actually loaded. A company without a
 * logo, a logo that fails (onError), and one whose storage host is slow to
 * answer all show the placeholder rather than a blank white square.
 */
export function CompanyLogo({ company, className, iconSize = 24 }) {
  const src = company?.logo_url ? resolveFileUrl(company.logo_url) : null;
  // Keyed by the URL, so a new logo starts again rather than inheriting the
  // last one's failure.
  const [state, setState] = useState({ src, status: 'loading' });
  const status = state.src === src ? state.status : 'loading';
  if (state.src !== src) setState({ src, status: 'loading' });

  // An image already in the cache can finish before React attaches onLoad.
  const imageRef = useCallback(
    (node) => {
      if (node?.complete && node.naturalWidth > 0) setState({ src, status: 'loaded' });
    },
    [src],
  );

  const showImage = src && status !== 'error';

  return (
    <span
      className={cn(
        'border-line bg-brand-50 text-brand-400 relative grid size-14 shrink-0 place-items-center overflow-hidden rounded-[12px] border',
        status === 'loaded' && 'bg-surface',
        className,
      )}
    >
      {status !== 'loaded' && <Icon name="building" size={iconSize} />}
      {showImage && (
        <img
          ref={imageRef}
          src={src}
          alt=""
          onLoad={() => setState({ src, status: 'loaded' })}
          onError={() => setState({ src, status: 'error' })}
          className={cn(
            'bg-surface absolute inset-0 size-full object-contain p-1.5 transition-opacity',
            status === 'loaded' ? 'opacity-100' : 'opacity-0',
          )}
        />
      )}
      <span className="sr-only">{company?.name ? `${company.name} logo` : 'Company logo'}</span>
    </span>
  );
}
